import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";

const CallCtx = createContext(null);

const CALL_STATES = {
  IDLE: "idle",
  OUTGOING: "outgoing", // we called, waiting for answer
  INCOMING: "incoming", // they called us
  ACTIVE: "active",
  ENDED: "ended",
};

function wsUrl() {
  const u = BACKEND_URL.replace(/^http/, "ws");
  return `${u}/api/ws/calls`;
}

export function CallProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const pendingIceRef = useRef([]);
  const [state, setState] = useState(CALL_STATES.IDLE);
  const [peer, setPeer] = useState(null); // {id, name, photo}
  const [callId, setCallId] = useState(null);
  const [kind, setKind] = useState("voice"); // 'voice' | 'video'
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [incomingOffer, setIncomingOffer] = useState(null);
  const [iceServers, setIceServers] = useState([
    { urls: "stun:stun.l.google.com:19302" },
  ]);

  // Fetch ICE servers once
  useEffect(() => {
    if (!user) return;
    api.get("/calls/ice-servers").then((r) => setIceServers(r.data.iceServers || iceServers)).catch(() => {});
  }, [user]);

  // Open WS when authed
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("spark_token");
    if (!token) return;
    let stopped = false;
    let retryDelay = 1000;
    const connect = () => {
      if (stopped) return;
      const ws = new WebSocket(`${wsUrl()}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        retryDelay = 1000;
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          handleSignal(msg);
        } catch {}
      };
      ws.onclose = () => {
        if (!stopped) setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 10000);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      stopped = true;
      try { wsRef.current?.close(); } catch {}
    };
    // eslint-disable-next-line
  }, [user]);

  const send = (payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    ws.send(JSON.stringify(payload));
    return true;
  };

  const cleanup = useCallback((next = CALL_STATES.IDLE) => {
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    pendingIceRef.current = [];
    setPeer(null);
    setCallId(null);
    setIncomingOffer(null);
    setKind("voice");
    setMicOn(true);
    setCamOn(true);
    setState(next);
    if (next !== CALL_STATES.IDLE) {
      setTimeout(() => setState(CALL_STATES.IDLE), 1500);
    }
  }, []);

  const createPc = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers });
    pc.onicecandidate = (e) => {
      if (e.candidate && peer && callId) {
        send({
          type: "ice",
          to: peer.id,
          call_id: callId,
          candidate: e.candidate.toJSON(),
        });
      }
    };
    pc.ontrack = (e) => {
      remoteStreamRef.current = e.streams[0];
      // Force re-render so UI hooks up
      setState((s) => s);
    };
    return pc;
    // eslint-disable-next-line
  }, [iceServers, peer, callId]);

  const startCall = useCallback(async (target, callKind = "voice") => {
    if (!target || state !== CALL_STATES.IDLE) return;
    const cid = crypto.randomUUID();
    setKind(callKind);
    setPeer(target);
    setCallId(cid);
    setState(CALL_STATES.OUTGOING);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: callKind === "video",
      });
      localStreamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          send({ type: "ice", to: target.id, call_id: cid, candidate: e.candidate.toJSON() });
        }
      };
      pc.ontrack = (e) => {
        remoteStreamRef.current = e.streams[0];
        setState((s) => s);
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({
        type: "call-offer",
        to: target.id,
        call_id: cid,
        kind: callKind,
        sdp: offer,
        from_name: user?.name,
        from_photo: user?.photos?.[0]?.storage_path || null,
      });
    } catch (e) {
      console.error("startCall failed", e);
      send({ type: "call-end", to: target.id, call_id: cid, reason: "media-error" });
      cleanup(CALL_STATES.ENDED);
    }
  }, [iceServers, state, user, cleanup]);

  const acceptCall = useCallback(async () => {
    if (!incomingOffer) return;
    setState(CALL_STATES.ACTIVE);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: incomingOffer.kind === "video",
      });
      localStreamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          send({
            type: "ice",
            to: incomingOffer.from,
            call_id: incomingOffer.call_id,
            candidate: e.candidate.toJSON(),
          });
        }
      };
      pc.ontrack = (e) => {
        remoteStreamRef.current = e.streams[0];
        setState((s) => s);
      };
      await pc.setRemoteDescription(incomingOffer.sdp);
      // Flush any pending ICE
      for (const c of pendingIceRef.current) {
        try { await pc.addIceCandidate(c); } catch {}
      }
      pendingIceRef.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({
        type: "call-answer",
        to: incomingOffer.from,
        call_id: incomingOffer.call_id,
        sdp: answer,
      });
      setIncomingOffer(null);
    } catch (e) {
      console.error("acceptCall failed", e);
      declineCall("media-error");
    }
    // eslint-disable-next-line
  }, [incomingOffer, iceServers]);

  const declineCall = useCallback((reason = "declined") => {
    if (!incomingOffer && !callId) return;
    const target = incomingOffer?.from || peer?.id;
    const cid = incomingOffer?.call_id || callId;
    send({ type: "call-decline", to: target, call_id: cid, reason });
    cleanup(CALL_STATES.ENDED);
  }, [incomingOffer, peer, callId, cleanup]);

  const endCall = useCallback(() => {
    if (peer && callId) {
      send({ type: "call-end", to: peer.id, call_id: callId });
    }
    cleanup(CALL_STATES.ENDED);
  }, [peer, callId, cleanup]);

  const toggleMic = useCallback(() => {
    const tracks = localStreamRef.current?.getAudioTracks() || [];
    tracks.forEach((t) => (t.enabled = !t.enabled));
    setMicOn(tracks[0]?.enabled ?? true);
  }, []);

  const toggleCam = useCallback(() => {
    const tracks = localStreamRef.current?.getVideoTracks() || [];
    tracks.forEach((t) => (t.enabled = !t.enabled));
    setCamOn(tracks[0]?.enabled ?? true);
  }, []);

  const handleSignal = (msg) => {
    if (msg.type === "call-offer") {
      // Refuse if already busy
      if (state !== CALL_STATES.IDLE) {
        send({ type: "call-decline", to: msg.from, call_id: msg.call_id, reason: "busy" });
        return;
      }
      setIncomingOffer({
        from: msg.from,
        sdp: msg.sdp,
        call_id: msg.call_id,
        kind: msg.kind || "voice",
      });
      setPeer({ id: msg.from, name: msg.from_name, photo: msg.from_photo });
      setKind(msg.kind || "voice");
      setCallId(msg.call_id);
      setState(CALL_STATES.INCOMING);
    } else if (msg.type === "call-answer") {
      const pc = pcRef.current;
      if (pc) {
        pc.setRemoteDescription(msg.sdp).then(async () => {
          for (const c of pendingIceRef.current) {
            try { await pc.addIceCandidate(c); } catch {}
          }
          pendingIceRef.current = [];
          setState(CALL_STATES.ACTIVE);
        });
      }
    } else if (msg.type === "ice") {
      const pc = pcRef.current;
      if (!pc || !pc.remoteDescription) {
        pendingIceRef.current.push(msg.candidate);
      } else {
        try { pc.addIceCandidate(msg.candidate); } catch {}
      }
    } else if (msg.type === "call-end" || msg.type === "call-decline" || msg.type === "call-unreachable") {
      cleanup(CALL_STATES.ENDED);
    }
  };

  return (
    <CallCtx.Provider
      value={{
        state, peer, kind, micOn, camOn,
        startCall, acceptCall, declineCall, endCall,
        toggleMic, toggleCam,
        localStream: localStreamRef.current,
        remoteStream: remoteStreamRef.current,
      }}
    >
      {children}
    </CallCtx.Provider>
  );
}

export const useCall = () => useContext(CallCtx);
export { CALL_STATES };
